import { COARSE_TEXT_HEIGHT } from "@/components/ui/_touch-targets";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DIETARY_TAGS,
  PARTY_CONTEXT_TAGS,
  RESERVATION_TAGS,
  type ReservationTagOption,
} from "@/lib/reservation-tags";
import type { ApiReservationTag } from "@/types/api";

/**
 * Editor de `Reservation.tags`. Controlado y sin efectos: el panel que lo
 * monta es el dueño del PATCH.
 *
 * Existe porque los tags ya no siempre los declara el comensal — el
 * backend también los infiere de la nota libre al crear la reserva, y una
 * inferencia equivocada ("queremos la paella de mariscos" → alergia a
 * mariscos) solo se podía corregir desde el admin de Django.
 *
 * Las dos filas están separadas a propósito: "Con mascota" no es una
 * dieta, y la fila "En la mesa" evita además que se lea como la
 * TableFeature "Mascota" que vive unos centímetros más arriba en el mismo
 * panel — el tag describe a la mesa, la feature restringe qué mesa puede
 * ocupar.
 */
export function ReservationTagPicker({
  value,
  onChange,
  disabled,
}: {
  value: ApiReservationTag[];
  onChange: (next: ApiReservationTag[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <TagGroup
        heading="Restricciones"
        options={DIETARY_TAGS}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      <TagGroup
        heading="En la mesa"
        options={PARTY_CONTEXT_TAGS}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Una fila de chips. Cada grupo solo conoce sus propios valores, así que
 * al emitir el cambio conserva los del otro grupo intactos y devuelve el
 * resultado en el orden canónico de `RESERVATION_TAGS`.
 *
 * Un valor que el backend agregó y el SPA todavía no conoce no se
 * renderiza, pero se arrastra al final: guardar desde acá no puede
 * borrar un tag que el operador ni siquiera vio.
 */
function TagGroup({
  heading,
  options,
  value,
  onChange,
  disabled,
}: {
  heading: string;
  options: readonly ReservationTagOption[];
  value: ApiReservationTag[];
  onChange: (next: ApiReservationTag[]) => void;
  disabled?: boolean;
}) {
  const owned = new Set(options.map((o) => o.value));
  const selected = value.filter((v) => owned.has(v));

  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[11px] text-text-secondary">{heading}</Label>
      <ToggleGroup
        multiple
        value={selected}
        onValueChange={(next) => {
          const merged = new Set<string>([...value.filter((v) => !owned.has(v)), ...next]);
          const known = RESERVATION_TAGS.filter((t) => merged.has(t.value)).map((t) => t.value);
          const unknown = [...merged].filter(
            (v) => !RESERVATION_TAGS.some((t) => t.value === v),
          ) as ApiReservationTag[];
          onChange([...known, ...unknown]);
        }}
        disabled={disabled}
        variant="outline"
        size="sm"
        className="w-full flex-wrap"
      >
        {options.map((o) => (
          <ToggleGroupItem
            key={o.value}
            value={o.value}
            aria-label={o.label}
            className={`${COARSE_TEXT_HEIGHT} text-[11px]`}
          >
            {o.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
